import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ReactComponent as Backspace } from '../media/images/svg/backspace.svg';
import { ReactComponent as Shift } from '../media/images/svg/shift.svg';
import { ReactComponent as ShiftLock } from '../media/images/svg/shift_lock.svg';
import { ReactComponent as Hide } from '../media/images/svg/keyboard_hide.svg';
import { ReactComponent as Up } from '../media/images/svg/arrow_upward.svg';
import { ReactComponent as Down } from '../media/images/svg/arrow_downward.svg';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   The on-screen keyboard.

   A lobby screen has no keyboard, and a phone's own keyboard is grey,
   covers half the screen and knows nothing about the field it is typing
   into. This one is part of the dashboard: it opens for any text field
   on any screen or modal, picks a layout from the field's type, shows
   what is being typed above the keys (the field itself is often behind
   them on a phone) and moves between fields with Next.

   It types into the real <input>, through the native value setter and
   an `input` event, so React's onChange and uncontrolled forms both see
   ordinary typing. The device keyboard is suppressed with
   inputmode="none" while ours is up, and restored after.

   Someone with a physical keyboard does not want this in the way: the
   first real keystroke folds it away until the field is tapped again.
   ───────────────────────────────────────────────────────────── */

const TEXT_TYPES = new Set(['text', 'search', 'email', 'tel', 'number', 'password', 'url', '']);

export function wantsKeyboard(el) {
    if (!el || el.disabled || el.readOnly) return false;
    if (el.closest?.('[data-native-keyboard]')) return false;
    if (el.tagName === 'TEXTAREA') return true;
    return el.tagName === 'INPUT' && TEXT_TYPES.has((el.getAttribute('type') || '').toLowerCase());
}

const LETTERS = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['{shift}', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '{bksp}']
];

const SYMBOLS = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['-', '/', ':', ';', '(', ')', '$', '&', '@', '"'],
    ['{more}', '.', ',', '?', '!', "'", '{bksp}']
];

const MORE = [
    ['[', ']', '{', '}', '#', '%', '^', '*', '+', '='],
    ['_', '\\', '|', '~', '<', '>', '€', '£', '¥', '•'],
    ['{123}', '.', ',', '?', '!', "'", '{bksp}']
];

/* Letters under the digits, as on a phone keypad, for phone numbers. */
const PAD_HINTS = { 2: 'ABC', 3: 'DEF', 4: 'GHI', 5: 'JKL', 6: 'MNO', 7: 'PQRS', 8: 'TUV', 9: 'WXYZ', 0: '+' };

function kindOf(el) {
    if (!el) return 'text';
    if (el.tagName === 'TEXTAREA') return 'textarea';
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    // Our own inputmode="none" is not the field's intent; its original
    // inputmode is kept in data-kb-mode.
    const attr = el.getAttribute('inputmode');
    const mode = (el.dataset.kbMode || (attr !== 'none' ? attr : '') || '').toLowerCase();
    if (type === 'number' || mode === 'numeric' || mode === 'decimal') return 'number';
    if (type === 'tel' || mode === 'tel') return 'tel';
    if (type === 'email' || mode === 'email') return 'email';
    if (type === 'url') return 'url';
    if (type === 'search') return 'search';
    if (type === 'password') return 'password';
    return 'text';
}

/* The text a reader would call this field: its label, else its
   placeholder, else its aria-label. */
function labelOf(el) {
    const wrap = el.closest('label');
    const fromWrap = wrap?.querySelector('.form-label')?.textContent;
    const byFor = el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`)?.textContent;
    return (fromWrap || byFor || el.getAttribute('aria-label') || el.placeholder || 'Text').trim();
}

/* Text fields sharing this one's form (or screen), in reading order. */
function siblingsOf(el) {
    const scope = el.form || el.closest('.modal__card, .screen-content, form') || document;
    return [...scope.querySelectorAll('input, textarea')].filter(f => wantsKeyboard(f) && !f.closest('[hidden], [aria-hidden="true"], [inert]'));
}

function setNativeValue(el, value) {
    const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
}

function selectionOf(el) {
    try {
        if (typeof el.selectionStart === 'number') return [el.selectionStart, el.selectionEnd];
    } catch (error) { /* number and email fields have no selection API */ }
    return null;
}

/* Scrolls the field clear of the keyboard, moving only its own scroll
   container: scrollIntoView would also slide the kiosk's clipped
   ancestors sideways. */
function revealAboveKeyboard(el, keyboardHeight) {
    const scroller = el.closest('.screen__scroll, .modal__body');
    if (!scroller) return;
    const rect = el.getBoundingClientRect();
    const visibleBottom = window.innerHeight - keyboardHeight - 20;
    const top = scroller.getBoundingClientRect().top + 12;
    if (rect.bottom > visibleBottom) scroller.scrollBy({ top: rect.bottom - visibleBottom, behavior: 'smooth' });
    else if (rect.top < top) scroller.scrollBy({ top: rect.top - top, behavior: 'smooth' });
}

export default function OnScreenKeyboard() {
    const reduced = useReducedMotion();
    const [target, setTarget] = useState(null);
    const [folded, setFolded] = useState(false);
    const [layer, setLayer] = useState('letters');
    const [shift, setShift] = useState('off');           // off | once | lock
    const [preview, setPreview] = useState('');
    const [pressed, setPressed] = useState(null);         // { key, touch }
    const [bounds, setBounds] = useState(0);
    const panel = useRef(null);
    const lastShift = useRef(0);
    const repeat = useRef(null);
    const numberBuffer = useRef(null);

    const kind = kindOf(target);
    const open = Boolean(target) && !folded;

    /* ── Following focus ──────────────────────────────────────── */

    useEffect(() => {
        const onFocusIn = event => {
            const el = event.target;
            if (!wantsKeyboard(el)) return;

            if (!el.dataset.kbPrevMode) el.dataset.kbPrevMode = el.getAttribute('inputmode') ?? '__none__';
            if (!el.dataset.kbMode && el.getAttribute('inputmode')) el.dataset.kbMode = el.getAttribute('inputmode');
            el.setAttribute('inputmode', 'none');

            numberBuffer.current = null;
            setTarget(el);
            setFolded(false);
            setLayer(kindOf(el) === 'tel' || kindOf(el) === 'number' ? 'pad' : 'letters');
            setShift(autoCapital(el) ? 'once' : 'off');
            setPreview(el.value);
        };

        const onFocusOut = event => {
            const el = event.target;
            if (!wantsKeyboard(el)) return;
            // The field leaving gets its own inputmode back straight away;
            // whichever field is gaining focus sets its own.
            restoreMode(el);
            // Let focus land first: moving to the next field is not closing.
            setTimeout(() => {
                if (!wantsKeyboard(document.activeElement)) {
                    setTarget(current => (current === el ? null : current));
                }
            }, 0);
        };

        /* A real keystroke means a real keyboard: get out of the way. */
        const onKeyDown = event => {
            if (event.isTrusted && wantsKeyboard(event.target) && event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
                setFolded(true);
            }
        };

        /* Tapping the field that folded the keyboard brings it back. */
        const onPointerDown = event => {
            if (wantsKeyboard(event.target) && event.target === document.activeElement) setFolded(false);
        };

        const onInput = event => {
            if (event.target === document.activeElement && wantsKeyboard(event.target)) setPreview(event.target.value);
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('input', onInput, true);
        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('input', onInput, true);
        };
    }, []);

    /* A field removed from the page (its screen closed) takes the
       keyboard with it; removal does not always fire focusout. */
    useEffect(() => {
        if (!target) return undefined;
        const id = setInterval(() => { if (!target.isConnected) setTarget(null); }, 400);
        return () => clearInterval(id);
    }, [target]);

    /* ── Making room ──────────────────────────────────────────── */

    useLayoutEffect(() => {
        const root = document.documentElement;
        if (!open || !panel.current) {
            root.classList.remove('kb-open');
            root.style.removeProperty('--kb-h');
            return undefined;
        }

        const measure = () => {
            const h = panel.current?.getBoundingClientRect().height || 0;
            setBounds(h);
            root.style.setProperty('--kb-h', `${Math.round(h)}px`);
        };

        root.classList.add('kb-open');
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(panel.current);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [open, layer, kind]);

    useEffect(() => {
        if (!open || !target || !bounds) return undefined;
        const id = setTimeout(() => revealAboveKeyboard(target, bounds), reduced ? 0 : 280);
        return () => clearTimeout(id);
    }, [open, target, bounds, reduced]);

    useEffect(() => () => {
        document.documentElement.classList.remove('kb-open');
        clearTimeout(repeat.current?.delay);
        clearInterval(repeat.current?.every);
    }, []);

    /* ── Typing ───────────────────────────────────────────────── */

    const edit = useCallback(fn => {
        const el = target;
        if (!el || !el.isConnected) return;

        /* A number field rejects a half-typed value such as "12." and
           would empty itself, so its text is kept here and only valid
           numbers are written back. */
        if (kindOf(el) === 'number' && el.getAttribute('type') === 'number') {
            const current = numberBuffer.current ?? el.value;
            const { next } = fn(current, current.length, current.length);
            numberBuffer.current = next;
            if (next === '' || !Number.isNaN(Number(next))) setNativeValue(el, next.replace(/\.$/, ''));
            setPreview(next);
            return;
        }

        const value = el.value;
        const sel = selectionOf(el);
        const [start, end] = sel || [value.length, value.length];
        const { next, caret } = fn(value, start, end);
        if (el.maxLength > 0 && next.length > el.maxLength) return;

        setNativeValue(el, next);
        if (sel) {
            try { el.setSelectionRange(caret, caret); } catch (error) { /* not selectable */ }
        }
        setPreview(next);
    }, [target]);

    const insert = useCallback(text => {
        edit((value, start, end) => ({ next: value.slice(0, start) + text + value.slice(end), caret: start + text.length }));
    }, [edit]);

    const backspace = useCallback(() => {
        edit((value, start, end) => {
            if (start !== end) return { next: value.slice(0, start) + value.slice(end), caret: start };
            if (start === 0) return { next: value, caret: 0 };
            return { next: value.slice(0, start - 1) + value.slice(start), caret: start - 1 };
        });
    }, [edit]);

    const fields = target ? siblingsOf(target) : [];
    const index = fields.indexOf(target);
    const next = index >= 0 ? fields[index + 1] : null;
    const prev = index > 0 ? fields[index - 1] : null;

    const enterLabel = kind === 'textarea' ? 'return'
        : next ? 'Next'
        : kind === 'search' ? 'Search'
        : target?.form ? 'Go' : 'Done';

    const hide = useCallback(() => {
        target?.blur();
        setTarget(null);
    }, [target]);

    const enter = useCallback(() => {
        if (!target) return;
        if (kind === 'textarea') return insert('\n');
        if (next) return next.focus();

        const form = target.form;
        target.blur();
        setTarget(null);
        if (form) {
            if (form.requestSubmit) form.requestSubmit();
            else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    }, [target, kind, next, insert]);

    const press = useCallback(key => {
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            try { navigator.vibrate(6); } catch (error) { /* not allowed */ }
        }

        switch (key) {
            case '{shift}': {
                const now = Date.now();
                if (shift === 'lock') setShift('off');
                else if (now - lastShift.current < 350) setShift('lock');
                else setShift(shift === 'once' ? 'off' : 'once');
                lastShift.current = now;
                return;
            }
            case '{bksp}': return backspace();
            case '{space}': insert(' '); break;
            case '{enter}': return enter();
            case '{123}': return setLayer('symbols');
            case '{more}': return setLayer('more');
            case '{abc}': return setLayer('letters');
            case '{dot}': return insert('.');
            case '{plus}': return insert('+');
            case '{com}': return insert('.com');
            default: {
                const upper = layer === 'letters' && shift !== 'off';
                insert(upper ? key.toUpperCase() : key);
                if (shift === 'once') setShift('off');
                return;
            }
        }

        // After a space, a sentence may be starting again.
        if (target && autoCapital(target, ' ')) setShift(current => (current === 'lock' ? current : 'once'));
    }, [shift, layer, backspace, insert, enter, target]);

    /* Keys act on pointerdown so a fast typist is not waiting for the
       release, and preventDefault keeps focus in the field. Backspace
       repeats while held. */
    const keyHandlers = key => ({
        onPointerDown: event => {
            event.preventDefault();
            setPressed({ key, touch: event.pointerType === 'touch' || event.pointerType === 'pen' });
            press(key);
            if (key === '{bksp}') {
                clearTimeout(repeat.current?.delay);
                clearInterval(repeat.current?.every);
                repeat.current = { delay: setTimeout(() => { repeat.current.every = setInterval(backspace, 55); }, 420) };
            }
        },
        onPointerUp: () => release(),
        onPointerLeave: () => release(),
        onPointerCancel: () => release(),
        onMouseDown: event => event.preventDefault(),
        onClick: event => {
            // Keyboard or assistive activation without a pointer.
            if (event.detail === 0) press(key);
        }
    });

    const release = () => {
        setPressed(null);
        clearTimeout(repeat.current?.delay);
        clearInterval(repeat.current?.every);
    };

    /* ── Layout ───────────────────────────────────────────────── */

    const rows = layer === 'symbols' ? SYMBOLS : layer === 'more' ? MORE : LETTERS;
    const upper = layer === 'letters' && shift !== 'off';

    const bottomRow = (() => {
        const toggle = layer === 'letters' ? '{123}' : '{abc}';
        if (kind === 'email') return [toggle, '@', '{space}', '.', '{enter}'];
        if (kind === 'url') return [toggle, '/', '{space}', '{com}', '{enter}'];
        return [toggle, '{space}', '{enter}'];
    })();

    const labelFor = key => ({
        '{shift}': shift === 'lock' ? 'Caps lock' : 'Shift',
        '{bksp}': 'Delete',
        '{space}': 'Space',
        '{enter}': enterLabel,
        '{123}': '123',
        '{more}': '#+=',
        '{abc}': 'ABC',
        '{com}': '.com',
        '{dot}': '.',
        '{plus}': '+'
    }[key] || (upper ? key.toUpperCase() : key));

    const renderKey = (key, extra = '') => {
        const special = key.startsWith('{');
        const isPressed = pressed?.key === key;
        const classes = [
            'kb__key',
            special && 'kb__key--fn',
            `kb__key--${special ? key.slice(1, -1) : 'char'}`,
            key === '{shift}' && shift !== 'off' && 'kb__key--on',
            key === '{shift}' && shift === 'lock' && 'kb__key--lock',
            key === '{enter}' && 'kb__key--primary',
            isPressed && 'kb__key--pressed',
            extra
        ].filter(Boolean).join(' ');

        let face = labelFor(key);
        if (key === '{bksp}') face = <Backspace aria-hidden="true" />;
        if (key === '{shift}') face = shift === 'lock' ? <ShiftLock aria-hidden="true" /> : <Shift aria-hidden="true" />;
        if (key === '{space}') face = <span className="kb__space-label">space</span>;
        if (extra.includes('kb__key--hinted')) face = <><span>{key}</span><small className="kb__hint">{PAD_HINTS[key]}</small></>;

        return (
            <button
                type="button"
                tabIndex={-1}
                key={key}
                className={classes}
                aria-label={labelFor(key)}
                {...keyHandlers(key)}
            >
                {face}
                {isPressed && pressed.touch && !special && (
                    <span className="kb__pop" aria-hidden="true">{labelFor(key)}</span>
                )}
            </button>
        );
    };

    const padRows = kind === 'tel'
        ? [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['{plus}', '0', '{bksp}']]
        : [['1', '2', '3'], ['4', '5', '6'], ['7', '8', '9'], ['{dot}', '0', '{bksp}']];

    const shown = kind === 'password' ? '•'.repeat(preview.length) : preview;

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="kb"
                    role="group"
                    aria-label="On-screen keyboard"
                    initial={reduced ? { opacity: 0 } : { y: '100%' }}
                    animate={reduced ? { opacity: 1 } : { y: 0 }}
                    exit={reduced ? { opacity: 0 } : { y: '100%' }}
                    transition={{ duration: 0.42, ease: EASE }}
                    onPointerDown={event => event.preventDefault()}
                >
                    <div className={`kb__panel kb__panel--${layer === 'pad' ? 'pad' : 'full'}`} ref={panel}>
                        <div className="kb__bar">
                            <div className="kb__nav">
                                <button type="button" tabIndex={-1} className="kb__nav-btn" aria-label="Previous field"
                                    disabled={!prev} onPointerDown={event => { event.preventDefault(); prev?.focus(); }}>
                                    <Up aria-hidden="true" />
                                </button>
                                <button type="button" tabIndex={-1} className="kb__nav-btn" aria-label="Next field"
                                    disabled={!next} onPointerDown={event => { event.preventDefault(); next?.focus(); }}>
                                    <Down aria-hidden="true" />
                                </button>
                            </div>

                            <div className="kb__field">
                                <span className="kb__label">{target ? labelOf(target) : ''}</span>
                                <span className="kb__preview" aria-live="polite">
                                    {shown ? <span className="kb__text"><bdi>{shown}</bdi></span> : <span className="kb__placeholder">{target?.placeholder || 'Start typing'}</span>}
                                    <span className="kb__caret" aria-hidden="true" />
                                </span>
                            </div>

                            <button type="button" tabIndex={-1} className="kb__done"
                                onPointerDown={event => { event.preventDefault(); hide(); }}>
                                <Hide aria-hidden="true" />
                                <span>Done</span>
                            </button>
                        </div>

                        {layer === 'pad' ? (
                            <div className="kb__pad">
                                {padRows.flat().map(key => renderKey(key, kind === 'tel' && PAD_HINTS[key] ? 'kb__key--hinted' : ''))}
                                <div className="kb__pad-foot">{renderKey('{enter}')}</div>
                            </div>
                        ) : (
                            <div className="kb__rows">
                                {rows.map((row, r) => (
                                    <div className={`kb__row kb__row--${r}`} key={`${layer}-${r}`}>
                                        {r === 1 && layer === 'letters' && <span className="kb__gap" />}
                                        {row.map(key => renderKey(key))}
                                        {r === 1 && layer === 'letters' && <span className="kb__gap" />}
                                    </div>
                                ))}
                                <div className="kb__row kb__row--bottom">
                                    {bottomRow.map(key => renderKey(key))}
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

/* Sentence-style fields start with a capital, and so does each new
   sentence. Addresses, codes and searches do not. */
function autoCapital(el, typed = '') {
    const kind = kindOf(el);
    if (!(kind === 'text' || kind === 'textarea')) return false;
    if ((el.getAttribute('autocapitalize') || '').toLowerCase() === 'off') return false;
    const value = (el.value || '') + typed;
    return value.length === 0 || /[.!?]\s$/.test(value) || /\n$/.test(value);
}

function restoreMode(el) {
    const prev = el.dataset.kbPrevMode;
    if (prev === undefined) return;
    if (prev === '__none__') el.removeAttribute('inputmode');
    else el.setAttribute('inputmode', prev);
    delete el.dataset.kbPrevMode;
}
